import { Col, Form, Input, Row } from 'antd';

import MediaCopyrightRadioButton from '../MediaCopyrightRadioButton';
import MediaTypeSection from '../MediaTypeSection';
import UploadArea from '../UploadArea';
import MediaCopyrightStatusRadioButton from '../MediaCopyrightStatusRadioButton';
import '../style.less';

const { TextArea } = Input;

/**
 * Açıklamalı form alanlarının `Form.Item`'ına verilmesi ZORUNLU olan sınıf. antd v4
 * label'ı `inline-flex` render ettiği için açıklama satırı başlığın yanına akar;
 * bu sınıf label'ı bloklaştırıp açıklamayı alt satıra indirir (bkz. `../style.less`).
 */
const FIELD_ITEM_CLASS = 'media-field-item';

/**
 * Input'un ALTINDA (`extra`) gösterilen ipucu metinleri için ortak stil. Label ile arasında net
 * bir görsel hiyerarşi kurmak üzere kasıtlı olarak daha küçük (12px) ve soluk (muted gri) tutulur;
 * aksi halde antd v4 `extra` metni label ile aynı boyutta akıp okunabilirliği düşürür.
 *
 * Yalnız yan yana render edilen Kaynak/Link için kullanılır: bu iki alanın açıklaması farklı
 * satır sayısına düştüğü için başlık altına alınsa input'ları dikeyde kayardı.
 */
const helperTextStyle = {
  display: 'block',
  marginTop: 4,
  fontSize: 12,
  lineHeight: 1.5,
  color: 'rgba(0, 0, 0, 0.45)',
};

/**
 * Başlık + hemen altında açıklama satırından oluşan form etiketi.
 *
 * Açıklamalar önce input'un ALTINDA (`extra`) veya tooltip içinde duruyordu; her ikisi de
 * gözden kaçtığı için doğrudan başlığın altına, input'un üstüne alındı.
 *
 * @param {Object} props
 * @param {string} props.title - Alan başlığı (label metni).
 * @param {string} props.description - Başlığın altında gösterilecek açıklama.
 */
function FieldLabel({ title, description }) {
  return (
    <span>
      {title}
      <span className="media-field-label__description">{description}</span>
    </span>
  );
}

/**
 * Medya teklifi ortak formu (tür, telif, açıklama, alt yazı, arşiv linki, kaynak, link, upload).
 *
 * @param {Object} props
 * @param {boolean} [props.readOnlyMediaType=false] - true ise dosya türü değiştirilemez.
 *   Düzenleme modallarında (EditMediaContentModal / SupervisorEditMediaContent) verilir; tür
 *   grup düzeyinde sabit olup BE `/revise`'de kabul edilmediği için düzenlemede salt-görüntülenir.
 *   Yeni ekleme akışında (default false) editable kalır.
 * @param {boolean} [props.readOnly=false] - true ise tüm form salt-görüntüleme: tüm alanlar (metin +
 *   telif radyoları + dosya türü) `disabled` — tutarlı gri görünüm; `UploadArea` yükleme/silme aksiyonları
 *   gizli. Read-only detay modunda (SupervisorEditMediaContent) verilir.
 * @param {boolean} [props.limitedEdit=false] - true ise yalnız **Alt Yazı**, **Kaynak** ve **Link**
 *   düzenlenebilir; diğer alanlar (tür, telif, açıklama, arşiv linki, upload) `disabled`. YM İmla/Editöryal
 *   redaksiyonu aşamalarında verilir. `readOnly` ile birlikte verilmez — `readOnly` zaten her şeyi kapatır;
 *   `limitedEdit` yalnız redaktör düzenleme modunda anlamlıdır.
 */
function MediaContentForm({ readOnlyMediaType = false, readOnly = false, limitedEdit = false }) {
  const form = Form.useFormInstance();

  const copyright = Form.useWatch('copyright', form);
  // Redaktör kısıtlı modu: çoğu alan kilitli; altyazı / kaynak / link yalnız `readOnly`'ye bağlı.
  const restricted = readOnly || limitedEdit;

  return (
    // Not: Bu bileşen kendi <Form>'unu render ETMEZ. Form instance'ı parent'tan
    // (Form.useFormInstance) gelir; aksi halde iç içe <form> DOM hatası oluşur.
    <>
      <MediaTypeSection disabled={readOnlyMediaType || restricted} />

      {/**
       * MediaCopyright form alanını gösterir
       */}
      <MediaCopyrightRadioButton disabled={restricted} />

      {/* Açıklama zorunlu DEĞİL (bilinçli): kullanıcı medya için açıklama girmek zorunda değil. */}
      <Form.Item
        className={FIELD_ITEM_CLASS}
        label={
          <FieldLabel
            title="Açıklama"
            description="Teklif ettiğiniz görsel/medya ile ilgili iletmek istediğiniz özet açıklamaları bu bölüme yazabilirsiniz. Bu açıklama altyazıdan daha geniş bir bilgi olup maddede gösterilmez; medyanın İSAM Medya Arşivi'ne eklenmesi durumunda kullanılabilir."
          />
        }
        name="description"
      >
        <TextArea
          rows={5}
          placeholder="Açıklama giriniz"
          disabled={restricted}
        />
      </Form.Item>

      <Form.Item
        className={FIELD_ITEM_CLASS}
        label={
          <FieldLabel
            title="Alt Yazı"
            description="Maddede yayınlanacak görsel/medya ile ilgili altyazı teklifiniz — yayında görünecek metindir."
          />
        }
        name="subtitle"
      >
        {/* `limitedEdit` modda açık kalan alanlardan biri (YM İmla/Editöryal). */}
        <TextArea
          rows={2}
          placeholder="Alt yazı giriniz"
          disabled={readOnly}
        />
      </Form.Item>

      <Form.Item
        label="Arşiv Linki"
        name="archiveLink"
        rules={[
          {
            type: 'url',
            message: 'Lütfen geçerli bir bağlantı giriniz (https://...)',
          },
        ]}
      >
        <Input
          placeholder="Arşiv linki"
          disabled={restricted}
        />
      </Form.Item>

      <>
        {/**
         * Kaynak ve Link yan yana render edilir. Kaynak yalnızca copyright === 'suggestion'
         * iken görünür; gizli olduğunda Link tek başına tam genişliği (md=24) kaplar.
         * `limitedEdit` modda ikisi de düzenlenebilir (altyazı ile birlikte).
         */}
        <Row
          gutter={24}
          align="top"
          style={{
            flexDirection: 'row',
            marginTop: 16,
          }}
        >
          {copyright === 'suggestion' && (
            <Col
              sm={24}
              md={12}
            >
              <Form.Item
                label="Kaynak"
                name="source"
                extra={
                  <span style={helperTextStyle}>
                    Teklif ettiğiniz görsel/medya kaynağı matbu ise künye bilgilerini giriniz.
                  </span>
                }
                rules={[
                  {
                    required: true,
                    message: 'Lütfen kaynak giriniz',
                  },
                ]}
              >
                <Input
                  placeholder="Örn: Sakıp Sabancı Müzesi Hat Koleksiyonu, nr. 140-0091"
                  disabled={readOnly}
                />
              </Form.Item>
            </Col>
          )}
          <Col
            sm={24}
            md={copyright === 'suggestion' ? 12 : 24}
          >
            {/* Link zorunlu DEĞİL (bilinçli): kaynak girilmiş olsa dahi internette örneği
                bulunmayabilir; yalnız girildiğinde format doğrulanır. */}
            <Form.Item
              label="Link"
              name="link"
              extra={
                <span style={helperTextStyle}>
                  Teklif ettiğiniz görsel/medya kaynağının bir örneği internet ortamında var ise
                  linkini giriniz.
                </span>
              }
              rules={[
                {
                  type: 'url',
                  message: 'Lütfen geçerli bir bağlantı giriniz (https://...)',
                },
              ]}
            >
              <Input
                placeholder="https://..."
                disabled={readOnly}
              />
            </Form.Item>
          </Col>
        </Row>

        {/**
         * UploadArea: dosya yükleme alanı. Boşken dragger; en az bir dosya varken
         * büyük kare thumbnail'lardan oluşan grid + "Ekle" kartı render eder.
         */}
        <UploadArea readOnly={restricted} />
      </>
      {copyright === 'suggestion' && <MediaCopyrightStatusRadioButton disabled={restricted} />}
    </>
  );
}
export default MediaContentForm;
